// services/intercom.js
import { generateUuidFromEmail } from '../helpers/trackingUtils'

let intercomModule = null
let isIntercomInitialized = false
let isProcessing = false

async function loadIntercomModule() {
  if (!intercomModule) {
    try {
      intercomModule = await import(
        /* webpackChunkName: "intercom" */ '@intercom/messenger-js-sdk'
      )
    } catch (error) {
      console.error('Failed to load Intercom module:', error)
      throw error
    }
  }
  return intercomModule
}

export async function initIntercom(cartData, showMessages = false) {
  if (isIntercomInitialized) {
    return
  }

  try {
    const { Intercom, show } = await loadIntercomModule()
    const intercomData = {
      app_id: INTERCOM_APP_ID,
      hide_default_launcher: true,
      custom_launcher_selector: '.cart-help-button',
      user_id: cartData?.initial_form_data?.uuid_from_email ?? null,
      name:
        cartData?.initial_form_data?.cleaned_contact_name &&
          cartData?.initial_form_data?.cleaned_contact_last_name
          ? `${cartData.initial_form_data.cleaned_contact_name} ${cartData.initial_form_data.cleaned_contact_last_name}`
          : (cartData?.initial_form_data?.cleaned_contact_name ?? null),
      email: cartData?.initial_form_data?.email_address ?? null
    }

    // Add `company` if it has non-null values
    if (
      cartData?.initial_form_data?.id &&
      cartData?.initial_form_data?.corporation_name &&
      cartData?.initial_form_data?.document_number &&
      COMPANY_UUID
    ) {
      const company = {
        company_id: cartData.initial_form_data.id,
        name: cartData.initial_form_data.corporation_name,
        document_number: cartData.initial_form_data.document_number,
        company_uuid: COMPANY_UUID
      }

      intercomData.company = company
    }

    // Add `custom_attributes` if it has non-null values
    const customAttributes = {}
    if (cartData?.initial_form_data?.registered_agent_name) {
      customAttributes.registered_agent_name =
        cartData.initial_form_data.registered_agent_name
    }

    if (Object.keys(customAttributes).length > 0) {
      intercomData.custom_attributes = customAttributes
    }
    // Call Intercom with the filtered data
    await Intercom(intercomData)

    isIntercomInitialized = true

    if (showMessages) {
      show()
    }
  } catch (error) {
    console.error('Failed to initialize Intercom:', error)
    throw error
  }
}

export async function sendIntercomContactUpdateEvent(
  cartData,
  formData,
  showMessages = false
) {
  if (isProcessing) {
    console.warn('Intercom update event is already in progress')
    return
  }
  isProcessing = true

  try {
    if (!formData?.email_address) {
      throw new Error('Email address is required')
    }

    const providedEmail = formData.email_address.trim()
    if (
      !providedEmail.match(
        /^(?!.*\.\.)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/
      )
    ) {
      throw new Error('Invalid email format')
    }

    if (isIntercomInitialized) {
      const { shutdown } = await loadIntercomModule()
      await shutdown()
      isIntercomInitialized = false
    }

    const newUuid = generateUuidFromEmail(providedEmail)

    const newCartData = {
      ...cartData,
      initial_form_data: {
        ...formData,
        email_address: providedEmail,
        uuid_from_email: newUuid,
        cleaned_contact_name: formData.first_name,
        cleaned_contact_last_name: formData.last_name
      }
    }
    await initIntercom(newCartData, showMessages)
  } catch (error) {
    console.error('Failed to send Intercom contact update event:', error)

    // Attempt cleanup if needed
    if (isIntercomInitialized) {
      const { shutdown } = await loadIntercomModule()
      await shutdown()
      isIntercomInitialized = false
    }

    throw error // Propagate the error for caller to handle
  } finally {
    isProcessing = false
  }
}

export function setupIntercomClickListener(selector, cartDataProvider) {
  const element = document.querySelector(selector)
  if (!element) {
    console.warn(`Element with selector "${selector}" not found.`)
    return
  }

  // Use a custom attribute to check if we've already attached a listener
  if (element.hasAttribute('data-intercom-listener')) {
    return
  }

  element.addEventListener('click', async (event) => {
    if (!isIntercomInitialized) {
      event.preventDefault() // Prevent default action if we need to initialize
      try {
        const cartData =
          typeof CART_DATA !== 'undefined' && CART_DATA
            ? CART_DATA // Use CART_DATA if it exists and is not undefined
            : typeof cartDataProvider === 'function'
              ? await cartDataProvider() // Fallback to cartDataProvider if it's a function
              : cartDataProvider || null
        if (!cartData) {
          throw new Error('Cart data is null or undefined')
        }

        await initIntercom(cartData, true)
        // After initialization, trigger the default action (e.g., open chat)
        element.click()
      } catch (error) {
        console.error('Failed to initialize Intercom on click:', error)
      }
    }
    // If Intercom is already initialized, the click event just passes through
  })

  // Mark the element so we don't attach multiple listeners
  element.setAttribute('data-intercom-listener', 'true')
}
